import request from '@/shared/api/request'
import type { AxiosRequestConfig } from 'axios'
import type { BatchRequestConfig } from '@/shared/api/use-api-batch'

// 角色相关接口
export interface Role {
  id: number
  name: string
  code: string
  description?: string
  parent_id?: number
  sort_order: number
  is_system: boolean
  is_default: boolean
  status: string
  created_at: string
  updated_at: string
  permissions: Permission[]
  parent?: Role
  children: Role[]
  user_count: number
}

export interface Permission {
  id: number
  name: string
  code: string
  description?: string
  module: string
  resource?: string
  action?: string
  status: string
  created_at: string
  updated_at: string
}

export interface RoleTemplate {
  id: number
  name: string
  description?: string
  permissions?: any
  is_builtin: boolean
  status: string
  created_at: string
  updated_at: string
}

export interface RoleListResponse {
  items: Role[]
  total: number
  page: number
  page_size: number
}

export interface RolePermissionMatrix {
  roles: Role[]
  permissions: Permission[]
  matrix: Record<number, {
    role: Role
    permissions: Set<number>
  }>
}

export interface CreateRoleData {
  name: string
  code: string
  description?: string
  parent_id?: number
  sort_order?: number
  permission_ids?: number[]
}

export interface UpdateRoleData {
  name?: string
  code?: string
  description?: string
  parent_id?: number
  sort_order?: number
  status?: string
  permission_ids?: number[]
}

export interface RoleQueryParams {
  skip?: number
  limit?: number
  search?: string
  status?: string
  is_system?: boolean
}

/** 角色下用户列表项（GET /roles/:id/users） */
export interface RoleUserItem {
  id: number
  username: string
  name: string
  email: string
}

export const roleApi = {
  // 获取角色列表
  getRoles: (
    params?: RoleQueryParams,
    config?: AxiosRequestConfig | BatchRequestConfig,
  ): Promise<RoleListResponse> => {
    return request.get('/roles', { params, ...config })
  },

  // 获取角色详情
  getRole: (id: number): Promise<Role> => {
    return request.get(`/roles/${id}`)
  },

  // 创建角色
  createRole: (data: CreateRoleData): Promise<Role> => {
    return request.post('/roles', data)
  },

  // 更新角色
  updateRole: (id: number, data: UpdateRoleData): Promise<Role> => {
    return request.put(`/roles/${id}`, data)
  },

  // 删除角色
  deleteRole: (
    id: number,
    config?: AxiosRequestConfig | BatchRequestConfig,
  ): Promise<void> => {
    return request.delete(`/roles/${id}`, config)
  },

  // 获取权限列表
  getPermissions: (
    module?: string,
    config?: AxiosRequestConfig | BatchRequestConfig,
  ): Promise<Permission[]> => {
    return request.get('/permissions', { params: { module }, ...config })
  },

  // 获取权限矩阵
  getPermissionMatrix: (): Promise<RolePermissionMatrix> => {
    return request.get('/permissions/matrix')
  },

  // 获取角色下的用户列表
  getRoleUsers: (roleId: number): Promise<RoleUserItem[]> => {
    return request.get(`/roles/${roleId}/users`)
  },

  // 为角色分配用户
  assignUsersToRole: (roleId: number, userIds: number[]): Promise<void> => {
    return request.post(`/roles/${roleId}/users`, { user_ids: userIds })
  },

  // 从角色中移除用户
  removeUserFromRole: (roleId: number, userId: number): Promise<void> => {
    return request.delete(`/roles/${roleId}/users/${userId}`)
  },

  // 获取角色模板
  getRoleTemplates: (): Promise<RoleTemplate[]> => {
    return request.get('/templates')
  },

  // 应用模板到角色
  applyTemplateToRole: (templateId: number, roleId: number): Promise<void> => {
    return request.post(`/templates/${templateId}/apply/${roleId}`)
  }
}

// 权限模块定义（与后端 permission.module 一致，用于角色管理权限树展示）
export const PERMISSION_MODULES = {
  project: '项目管理',
  video: '视频译配',
  document: '文档翻译',
  task: '任务管理',
  system: '系统管理',
  speech_translation: '会议翻译',
  classroom: '智慧教室',
  classroom_v3: '智慧教室 V3',
  demo: 'Demo',
  teaching: '协同教研',
}

export const PERMISSION_NAME_PREFIX = {
  project: '项目管理',
  video: '视频译配',
  document: '文档翻译',
  task: '任务管理',
  system: '系统管理',
  speech_translation: '会议翻译',
  classroom: '智慧教室',
  classroom_v3: '智慧教室V3',
  demo: 'Demo',
  teaching: '协同教研',
}

export const CONTRACT_PERMISSION_MODULES = ['classroom', 'classroom_v3', 'speech_translation'] as const

// 权限操作定义
export const PERMISSION_ACTIONS = {
  view: '查看',
  list: '列表',
  create: '创建',
  edit: '编辑',
  delete: '删除',
  export: '导出',
  upload: '上传',
  assign: '分配',
  manage: '管理',
  send: '发送',
  chat: '对话',
  complete: '完成'
}